import { MACDESK_ARM64, MACDESK_X64, WINDOWDESK } from '@/assets/constant/default';

// 工作台发版目录：latest.yml / latest-mac.yml 由 electron-builder 随包生成
const WORKBENCH_FEED = 'https://ustatic.hudongmiao.com/joymew-desk/stable/';
const WORKBENCH_MAC_FEED = 'https://ustatic.hudongmiao.com/joymew-desk/stable-mac/';

/**
 * 识别官网当前所处的桌面端环境。
 * 官网会被旧版「嗨喵桌面」（hudongmiao，已停止维护）以 iframe 内嵌，
 * 也是新版「嗨喵工作台」的主页（以内嵌标签页加载）。
 * - 旧桌面端不覆盖 UA，Electron 默认 UA 自带 `hudongmiao/<版本号>`
 * - 浏览器直接访问时 UA 不含 Electron/
 */
export function detectDesktopEnv() {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  const legacyMatch = ua.match(/\bhudongmiao\/([\d.]+)/i);
  return {
    isElectron: /Electron\//.test(ua),
    isLegacyDesktop: !!legacyMatch,
    legacyVersion: legacyMatch ? legacyMatch[1] : '',
  };
}

/**
 * 识别新版「嗨喵工作台」及其版本号。
 * 工作台把应用名设为「嗨喵工作台」，Electron 默认 UA 会带 `应用名/版本`，
 * 且该 token 紧贴 `Chrome/` 之前；应用名可能被编码或改写，因此版本号
 * 优先取具名匹配，退而求其次解析 Chrome/ 前的那个 token。
 */
export function detectWorkbenchEnv() {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  if (!/Electron\//.test(ua)) return { isWorkbench: false, version: '' };
  if (/\bhudongmiao\//i.test(ua)) return { isWorkbench: false, version: '' };
  const named = ua.match(/嗨喵工作台\/([\d.]+)/);
  if (named) return { isWorkbench: true, version: named[1] };
  const generic = ua.match(/([^\s/]+)\/([\d.]+)\s+Chrome\//);
  if (generic) return { isWorkbench: true, version: generic[2] };
  // UA 结构异常时宁可认为识别失败，由调用方决定不弹
  return { isWorkbench: true, version: '' };
}

/** 与顶栏下载逻辑一致：识别访问者是否在使用 macOS 浏览器。 */
export function isMacOsUserAgent(userAgent) {
  const ua = String(userAgent || (typeof navigator !== 'undefined' && navigator.userAgent) || '').toLowerCase();
  return ua.indexOf('mac') > 0 && ua.indexOf('os') > 0;
}

/** 逐段比较版本号：a<b 返回 -1，a=b 返回 0，a>b 返回 1。 */
export function compareVersions(a, b) {
  const pa = String(a || '').split('.');
  const pb = String(b || '').split('.');
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i += 1) {
    const diff = (parseInt(pa[i], 10) || 0) - (parseInt(pb[i], 10) || 0);
    if (diff) return diff > 0 ? 1 : -1;
  }
  return 0;
}

/** 用 XHR 拉取更新清单：旧桌面端内核是 Chromium 66，兼容性最稳。 */
function fetchManifestText(feedBase, fileName) {
  return new Promise((resolve) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', `${feedBase}${fileName}?t=${Date.now()}`, true);
      xhr.timeout = 8000;
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(String(xhr.responseText || ''));
        } else {
          resolve('');
        }
      };
      xhr.onerror = () => resolve('');
      xhr.ontimeout = () => resolve('');
      xhr.send();
    } catch (e) {
      resolve('');
    }
  });
}

function listYmlArtifactUrls(text) {
  return [...String(text).matchAll(/^\s+-\s+url:\s+(\S+)\s*$/gm)].map((match) => match[1]);
}

/**
 * 解析 Windows 工作台的最新下载信息（安装包直链 + 版本号）。
 * 网络异常时回退到 WINDOWDESK 常量（version 为空串）。
 */
export function resolveWorkbenchDownloadInfo() {
  return fetchManifestText(WORKBENCH_FEED, 'latest.yml').then((text) => {
    const pathMatch = /^path:\s*(\S+)\s*$/m.exec(text);
    const versionMatch = /^version:\s*(\S+)\s*$/m.exec(text);
    if (pathMatch) {
      return {
        url: WORKBENCH_FEED + pathMatch[1],
        version: versionMatch ? versionMatch[1] : '',
      };
    }
    return { url: WINDOWDESK, version: '' };
  });
}

/**
 * 解析 Mac 工作台最新 DMG（Intel x64 与 Apple 芯片 arm64）。
 * 官网手动安装走 dmg；zip 留给客户端在线更新。
 * 网络异常时回退到 MACDESK_X64 / MACDESK_ARM64 常量。
 */
export function resolveWorkbenchMacDownloadInfo() {
  return fetchManifestText(WORKBENCH_MAC_FEED, 'latest-mac.yml').then((text) => {
    const versionMatch = /^version:\s*(\S+)\s*$/m.exec(text);
    const version = versionMatch ? versionMatch[1] : '';
    const urls = listYmlArtifactUrls(text);
    const x64Name = urls.find((name) => /-x64\.dmg$/i.test(name));
    const arm64Name = urls.find((name) => /-arm64\.dmg$/i.test(name));
    if (x64Name || arm64Name) {
      return {
        version,
        x64Url: x64Name ? WORKBENCH_MAC_FEED + x64Name : MACDESK_X64,
        arm64Url: arm64Name ? WORKBENCH_MAC_FEED + arm64Name : MACDESK_ARM64,
      };
    }
    return {
      version: '',
      x64Url: MACDESK_X64,
      arm64Url: MACDESK_ARM64,
    };
  });
}

/** 弹窗等只需一个 Mac 直链时：无法可靠区分芯片，优先 Apple 芯片包（Intel 机可 Rosetta，反之不行）。 */
export function pickMacDownloadUrl(macInfo) {
  return macInfo.arm64Url || macInfo.x64Url || MACDESK_ARM64;
}
